import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';
import { useAuthStore } from '../../../stores/auth-store';
import { useToastStore } from '../../../stores/toast-store';

import type { CoachingCreatedWire } from './types';
import type { WsEventEnvelope } from '../../../lib/websocket/events';

export const COACHING_TOAST_MS = 12_000;

export function coachingToastDescription(d: CoachingCreatedWire): string {
  const quem = d.fromUserName ?? 'Supervisão';
  const onde =
    d.conversationLabel !== undefined && d.conversationLabel !== null
      ? `conversa com ${d.conversationLabel}`
      : 'uma conversa sua';
  return `${quem} · ${onde}`;
}

export function coachingConversationPath(conversationId: string): string {
  return `/atendimento/conversation/${encodeURIComponent(conversationId)}?supervisao=1`;
}

/**
 * Aviso global quando chega coaching e o operador está em OUTRA tela (caso 3 do TL):
 * diz de qual cliente é e o botão «Abrir» leva à conversa certa já com o painel de
 * orientações aberto. Se a conversa já está na tela, a faixa acima do Resumo Vivo
 * acende — o aviso seria redundante. Montado no AppShell.
 */
export function useCoachingToast(): void {
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useAuthStore((s) => s.user?.id);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    return subscribe(
      WS_EVENTS.COACHING_MESSAGE_CREATED,
      (envelope: WsEventEnvelope<CoachingCreatedWire>) => {
        const d = envelope.data;
        if (me === undefined || d.toUserId !== me || !d.visibleToAttendant) return;
        if (d.conversationId !== undefined && pathRef.current.includes(d.conversationId)) return;
        const conversationId = d.conversationId;
        useToastStore.getState().push({
          variant: 'info',
          title: 'Orientação da supervisão',
          description: coachingToastDescription(d),
          durationMs: COACHING_TOAST_MS,
          ...(conversationId !== undefined && {
            actionLabel: 'Abrir',
            onAction: () => navigate(coachingConversationPath(conversationId)),
          }),
        });
      },
    );
  }, [subscribe, me, navigate]);
}
